import { Card, CardContent, Collapse, Table, TableBody, TableContainer, TableHead, Typography } from '@mui/material';

import { EntityListWithPagination, EntityWithId } from 'src/types/baseEntities';
import { ITableColumn, TableBodyEntityList, TableCardHeader, TableRowHeader } from './TableLayouts';
import { Pagination } from './Paginador';
import * as React from "react";


export interface ITableWithPagingProps<T extends EntityWithId> {
    title: string,
    subtitle?: string | null,
    entityPaginada: EntityListWithPagination<T> | null | undefined,
    columns: ITableColumn[],
    isLoading: boolean,
    error: any,
    onPaging: (actualPage: number) => void,
    onNewRegister?: () => void
    onReload?: () => void,
    actions?: React.ReactNode,
    selectable?: boolean,
    handleSelectedAction?: (idList: readonly number[]) => void,
    selectedActionName?: string,
    selectedActionIcon?: React.ReactNode,
}

export function TableWithPaging<T extends EntityWithId>(props: ITableWithPagingProps<T>) {

    return (
        <Typography component="div"
            sx={{
                flexGrow: 1,
                marginTop: 5,
            }}
        >
            <Card>
                <TableCardHeader title={props.title}
                    subtitle={props.subtitle}
                    onNewRegister={props.onNewRegister}
                    onReload={props.onReload}
                    onSelectedAction={props.handleSelectedAction}
                    actions={props.actions}
                />

                <CardContent
                    sx={{
                        padding: 0
                    }}>
                    <TableContainer>
                        <Table
                            sx={{
                                width: 'max-content',
                                minWidth: '100%',
                            }}
                        >
                            <TableHead>
                                <TableRowHeader columns={props.columns} />
                            </TableHead>
                            <TableBody>
                                <TableBodyEntityList<T> entityList={props.entityPaginada?.lista}
                                    columns={props.columns}
                                    isLoading={props.isLoading}
                                    error={props.error}
                                />
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <Pagination
                        entityPagination={props.entityPaginada?.paginacion}
                        isLoading={props.isLoading}
                        onPaging={props.onPaging} />
                </CardContent>
            </Card>
        </Typography>
    )
}


export interface ITableWithPagingAndFilterProps<T extends EntityWithId> extends ITableWithPagingProps<T> {
    children: React.ReactElement,
    showChildren?: boolean,
    onShowChildren?: () => void,
    childrenName?: string //el nombre que va a mostrar en el boton
}

interface TableRowSelectionContextType {
    selected: readonly number[];
    isSelected: (id: number) => boolean;
    handleSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleSelect: (event: React.MouseEvent<unknown>, id: number) => void;
    selectable?: boolean;
    numRows: number;
}

const defaultContextValue: TableRowSelectionContextType = {
    selected: [],
    isSelected: () => false,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    handleSelectAllClick: () => {
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    handleSelect: () => {
    },
    selectable: false,
    numRows: 0,
};

export const TableRowSelectionContext = React.createContext<TableRowSelectionContextType>(defaultContextValue);

export function TableWithPagingAndFilter<T extends EntityWithId>(props: ITableWithPagingAndFilterProps<T>) {
    const [selected, setSelected] = React.useState<readonly number[]>([]);

    const isSelected = (id: number) => selected.indexOf(id) !== -1;

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = props.entityPaginada?.lista.map((n) => n.id) || [];
            setSelected(newSelected);

            return;
        }
        setSelected([]);
    };

    const handleSelect = (event: React.MouseEvent<unknown>, id: number) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected: readonly number[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1),
            );
        }
        setSelected(newSelected);
    };

    return (
        <Typography component="div"
            sx={{
                flexGrow: 1,
                marginTop: 5,
                overflowX: 'auto'
            }}
        >
            <Card>
                <TableRowSelectionContext.Provider value={{
                    selected,
                    isSelected,
                    handleSelectAllClick,
                    handleSelect,
                    selectable: props.selectable,
                    numRows: props.entityPaginada?.lista.length || 0
                }}>
                    <TableCardHeader title={props.title}
                        onNewRegister={props.onNewRegister}
                        onReload={props.onReload}
                        onToggleShowChildren={props.onShowChildren}
                        childrenName={props.childrenName}
                        onSelectedAction={props.handleSelectedAction}
                        selectedActionName={props.selectedActionName}
                        selectedActionIcon={props.selectedActionIcon}
                    />

                    <CardContent sx={{ padding: 0, overflowX: 'auto' }}>

                        <Collapse in={props.showChildren} timeout={500}
                        >
                            {props.children}
                        </Collapse>

                        <TableContainer sx={{ width: '100%' }}>
                            <Table stickyHeader
                                sx={{ width: '100%   ' }}
                            >
                                <TableHead>
                                    <TableRowHeader
                                        columns={props.columns}
                                    />
                                </TableHead>
                                <TableBody>
                                    <TableBodyEntityList<T> entityList={props.entityPaginada?.lista}
                                        columns={props.columns}
                                        isLoading={props.isLoading}
                                        error={props.error}
                                    />
                                </TableBody>

                                <Pagination
                                    entityPagination={props.entityPaginada?.paginacion}
                                    isLoading={props.isLoading}
                                    onPaging={props.onPaging} />

                            </Table>
                        </TableContainer>
                    </CardContent>
                </TableRowSelectionContext.Provider>
            </Card>
        </Typography>
    )
}